import { Fragment, useState, useEffect, useCallback } from 'react';
import { useBranch } from '../contexts/BranchContext';
import { useConfirm } from '../contexts/ConfirmContext';
import { useSocket } from '../hooks/useSocket';
import { getAllTransactions } from '../utils/offlineDb';
import Toast from '../components/Toast';
import Modal from '../components/Modal';
import CashDrawerOpenButton from '../components/CashDrawerOpenButton';
import CashDenominationTapGrid from '../components/CashDenominationTapGrid';
import {
  DENOMINATIONS,
  emptyCounts,
  sumCounts,
  hasAnyCount,
  countOf,
  denominationLabel,
  formatBaht
} from '../utils/denominations';

const NAME_KEY = 'cashDrawerCounterName';

// เหมือน countOf แต่ไม่ clamp ติดลบทิ้ง — countOf ออกแบบมาไว้อ่านจำนวนแบงก์จริง (ติดลบไม่ได้)
// ส่วน "ควรมี" เป็นค่าคำนวณ (ตอนเปิด+รับเข้า−ทอนออก) ที่ติดลบได้จริงเวลาลิ้นชักขาดแบงก์ชนิดนั้น
// ต้องโชว์ค่าติดลบตรงๆ ไม่ใช่ปัดเป็น 0 (ดูสเปก: "แถวที่ 'ควรมี' ติดลบ: สีแดง")
const signedCount = (counts, value) => {
  const n = Number(counts?.[String(value)]);
  return Number.isFinite(n) ? Math.trunc(n) : 0;
};

const readSavedName = () => {
  try { return localStorage.getItem(NAME_KEY) || ''; } catch { return ''; }
};
const saveName = (name) => {
  try { if (name) localStorage.setItem(NAME_KEY, name); } catch { /* private mode — ไม่จำชื่อก็ไม่เป็นไร */ }
};

const fmtDateTime = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleString('th-TH', {
    timeZone: 'Asia/Bangkok',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// ส่วนต่าง: ติดลบ = เงินขาด, บวก = เงินเกิน, 0 = ตรงพอดี
function differenceView(diff) {
  if (diff === null || diff === undefined) return { text: '—', color: 'var(--muted)' };
  if (diff === 0) return { text: 'ตรงพอดี', color: 'var(--ok)' };
  if (diff < 0) return { text: `ขาด ${formatBaht(Math.abs(diff))} ฿`, color: 'var(--danger)' };
  return { text: `เกิน ${formatBaht(diff)} ฿`, color: 'var(--warn)' };
}

const primaryButton = (disabled) => ({
  minHeight: '48px',
  padding: '0 1.5rem',
  borderRadius: '10px',
  border: 'none',
  fontWeight: 700,
  fontSize: '1rem',
  cursor: disabled ? 'not-allowed' : 'pointer',
  background: disabled ? 'var(--surface-2)' : 'var(--accent)',
  color: disabled ? 'var(--muted)' : '#1a1206'
});

const secondaryButton = {
  minHeight: '48px',
  padding: '0 1.25rem',
  borderRadius: '10px',
  border: '1px solid var(--border-strong)',
  background: 'transparent',
  color: 'var(--text-2)',
  fontWeight: 600,
  fontSize: '1rem',
  cursor: 'pointer'
};

const stepButton = {
  width: '44px',
  height: '44px',
  borderRadius: '10px',
  border: '1px solid var(--border-strong)',
  background: 'var(--surface)',
  color: 'var(--text)',
  fontSize: '1.4rem',
  lineHeight: 1,
  cursor: 'pointer',
  flexShrink: 0
};

const cardStyle = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: '16px',
  padding: '1.25rem',
  marginBottom: '1.25rem'
};

function CountsForm({ counts, onChange, hints }) {
  const setCount = (value, raw) => onChange({ ...counts, [String(value)]: raw });
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(270px, 1fr))', gap: '0.6rem' }}>
      {DENOMINATIONS.map(d => {
        const n = countOf(counts, d.value);
        return (
          <div
            key={d.value}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.6rem 0.75rem',
              background: 'var(--surface-2)',
              border: '1px solid var(--border)',
              borderRadius: '12px'
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, color: 'var(--text)', whiteSpace: 'nowrap' }}>{denominationLabel(d)}</div>
              <div style={{ fontSize: '0.8rem', color: 'var(--muted)' }}>= {formatBaht(n * d.value)} ฿</div>
              {hints && <div style={{ fontSize: '0.75rem', color: 'var(--accent)' }}>ควรมี {signedCount(hints, d.value)}</div>}
            </div>
            <button
              type="button"
              aria-label={`${denominationLabel(d)} ลด`}
              style={stepButton}
              onClick={() => setCount(d.value, String(Math.max(0, n - 1)))}
            >
              −
            </button>
            <input
              className="dark-input"
              type="text"
              inputMode="numeric"
              aria-label={`จำนวน ${denominationLabel(d)}`}
              value={counts[String(d.value)] ?? ''}
              placeholder="0"
              onChange={(e) => setCount(d.value, e.target.value.replace(/\D/g, '').slice(0, 6))}
              style={{ width: '58px', minHeight: '44px', textAlign: 'center', fontWeight: 700, fontSize: '1.05rem', padding: '0 0.25rem' }}
            />
            <button
              type="button"
              aria-label={`${denominationLabel(d)} เพิ่ม`}
              style={stepButton}
              onClick={() => setCount(d.value, String(n + 1))}
            >
              +
            </button>
          </div>
        );
      })}
    </div>
  );
}

function SummaryRow({ label, value, color, strong }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', padding: '0.35rem 0', fontSize: strong ? '1.1rem' : '0.95rem' }}>
      <span style={{ color: 'var(--text-2)' }}>{label}</span>
      <span style={{ color: color || 'var(--text)', fontWeight: strong ? 700 : 600, textAlign: 'right' }}>{value}</span>
    </div>
  );
}

function CountsBreakdown({ title, counts }) {
  return (
    <div style={{ minWidth: '200px' }}>
      <div style={{ fontWeight: 700, marginBottom: '0.35rem', color: 'var(--text)' }}>{title}</div>
      {DENOMINATIONS.filter(d => countOf(counts, d.value) > 0).map(d => (
        <div key={d.value} style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', fontSize: '0.9rem', color: 'var(--text-2)' }}>
          <span>{denominationLabel(d)} × {countOf(counts, d.value)}</span>
          <span>{formatBaht(countOf(counts, d.value) * d.value)}</span>
        </div>
      ))}
      {!hasAnyCount(counts) && <div style={{ color: 'var(--muted)', fontSize: '0.9rem' }}>ไม่มีข้อมูล</div>}
    </div>
  );
}

/**
 * "ควรมี" ต่อมูลค่า = ตอนเปิด + รับเข้า − ทอนออก (จาก cashBreakdown ของบิลเงินสดที่ระบุไว้)
 * บิลที่ไม่ได้ระบุ breakdown ไม่มีทางรู้ว่าเป็นแบงก์ชนิดไหน จึงแสดงแยกเป็นยอดบาทรวมท้ายตาราง
 * แทน — ผลรวม "ควรมี" ทุกแถว + ยอดบิลที่ไม่ระบุ ต้องเท่ากับ expectedNow เสมอ (คำนวณฝั่ง backend
 * ด้วยการลบเลขกลับ ไม่ได้ sum ยอดบิลจริง กันกรณี breakdown ที่พนักงานกรอกไม่ตรงกับยอดบิลเป๊ะ)
 */
function ExpectedBreakdownCard({ shouldHaveCounts, cashFlow, expectedNow, otherAdjustments }) {
  if (!shouldHaveCounts || !cashFlow) return null;

  // แบงก์ใหญ่ → เหรียญเล็ก เหมือนหน้าคิดเงิน (Checkout.jsx) — แสดงครบทุกมูลค่าเสมอ 9 แถว
  // ไม่ซ่อนแถว 0 อีกต่อไป (ผู้ใช้ยกเลิกเงื่อนไขซ่อนที่สั่งไว้ก่อนหน้า) ให้จางแทนถ้าไม่มีอะไรเลย
  const rows = [...DENOMINATIONS].reverse().map(d => ({
    d,
    open: countOf(shouldHaveCounts.opening, d.value),
    inn: countOf(cashFlow.received, d.value),
    out: countOf(cashFlow.change, d.value),
    other: signedCount(otherAdjustments, d.value), // เบิกใช้/แลกเงิน/เติม/นำออก — ติดลบได้ ไม่ clamp
    should: signedCount(shouldHaveCounts.should, d.value)
  }));

  const specifiedTotal = rows.reduce((sum, r) => sum + r.should * r.d.value, 0);
  const missingCount = cashFlow.billsWithoutBreakdown || 0;

  return (
    <div style={cardStyle}>
      <h3 style={{ margin: '0 0 0.75rem' }}>แบงก์ที่ควรมีในลิ้นชักตอนนี้</h3>
      <div style={{ overflowX: 'auto' }}>
        <table className="table-dark" style={{ width: '100%', minWidth: '560px', borderCollapse: 'collapse', background: 'transparent' }}>
          <thead>
            <tr>
              {['ชนิดเงิน', 'ตอนเปิด', '+รับเข้า', '−ทอนออก', '±อื่นๆ', '= ควรมี'].map((h, i) => (
                <th key={h} style={{ padding: '0.5rem', textAlign: i === 0 ? 'left' : 'right', background: 'var(--surface-2)', color: 'var(--text-2)', fontWeight: 700, fontSize: '0.85rem' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const isEmpty = r.open === 0 && r.inn === 0 && r.out === 0 && r.other === 0;
              const labelColor = isEmpty ? 'var(--muted)' : 'var(--text)';
              const openColor = isEmpty ? 'var(--muted)' : 'var(--text-2)';
              const otherColor = r.other === 0 ? 'var(--muted)' : (r.other > 0 ? 'var(--ok)' : 'var(--danger)');
              const shouldColor = isEmpty ? 'var(--muted)' : (r.should < 0 ? 'var(--danger)' : 'var(--text)');
              return (
                <tr key={r.d.value}>
                  <td style={{ padding: '0.5rem', background: 'var(--surface-2)', color: labelColor, borderBottom: '1px solid var(--border)' }}>{denominationLabel(r.d)}</td>
                  <td style={{ padding: '0.5rem', background: 'var(--surface-2)', textAlign: 'right', color: openColor, borderBottom: '1px solid var(--border)' }}>{r.open}</td>
                  <td style={{ padding: '0.5rem', background: 'var(--surface-2)', textAlign: 'right', color: r.inn === 0 ? 'var(--muted)' : 'var(--ok)', borderBottom: '1px solid var(--border)' }}>{r.inn === 0 ? '—' : `+${r.inn}`}</td>
                  <td style={{ padding: '0.5rem', background: 'var(--surface-2)', textAlign: 'right', color: r.out === 0 ? 'var(--muted)' : 'var(--danger)', borderBottom: '1px solid var(--border)' }}>{r.out === 0 ? '—' : `−${r.out}`}</td>
                  <td style={{ padding: '0.5rem', background: 'var(--surface-2)', textAlign: 'right', color: otherColor, borderBottom: '1px solid var(--border)' }}>{r.other === 0 ? '—' : (r.other > 0 ? `+${r.other}` : r.other)}</td>
                  <td style={{ padding: '0.5rem', background: 'var(--surface-2)', textAlign: 'right', fontWeight: 700, borderBottom: '1px solid var(--border)', color: shouldColor }}>{r.should}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={{ marginTop: '0.75rem', fontWeight: 700, color: 'var(--text)' }}>
        รวมที่ระบุแบงก์ได้ {formatBaht(specifiedTotal)} ฿
      </div>
      {missingCount > 0 && (
        <div style={{ marginTop: '0.5rem', padding: '0.75rem 1rem', background: '#1d1a12', border: '1px solid #4a3c1c', color: 'var(--warn)', borderRadius: '10px', fontSize: '0.9rem' }}>
          ⚠ มี {missingCount} บิล ({formatBaht(cashFlow.amountWithoutBreakdown)} ฿) ที่ไม่ได้ระบุแบงก์ รวมแล้วควรมี {formatBaht(expectedNow)} ฿
        </div>
      )}
    </div>
  );
}

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const movementFormCardStyle = { marginBottom: '0.75rem' };
const movementInputStyle = { width: '100%', minHeight: '44px', marginBottom: '0.75rem' };

/** เบิกใช้ (paid_out) ขั้น 1 — แตะแบงก์ที่หยิบออก + เหตุผล แล้วเลือกได้ว่าจะ "บันทึก" (เบิกค้าง
 * รอคืนเงินทอนทีหลัง) หรือ "ใช้พอดี ไม่มีเงินทอน" (ปิดรายการทันที ไม่มีขั้น 2) */
function PaidOutForm({ onSubmit, onFullUse, submitting }) {
  const [out, setOut] = useState(emptyCounts());
  const [reason, setReason] = useState('');
  const amount = sumCounts(out);
  const ready = amount > 0 && reason.trim().length > 0;

  return (
    <div>
      <div style={movementFormCardStyle}>
        <div style={{ fontWeight: 600, marginBottom: '0.5rem', color: 'var(--text)' }}>แตะแบงก์ที่หยิบออก</div>
        <CashDenominationTapGrid counts={out} onChange={setOut} />
      </div>
      <div style={{ fontWeight: 700, marginBottom: '0.75rem', color: 'var(--text)' }}>รวม {formatBaht(amount)} ฿</div>
      <input
        className="dark-input"
        placeholder="เหตุผล (เช่น ซื้อน้ำแข็ง) — บังคับ"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        style={movementInputStyle}
      />
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        <button type="button" disabled={submitting || !ready} style={primaryButton(submitting || !ready)} onClick={() => onSubmit({ countsOut: out, reason })}>
          บันทึก (เบิกค้าง รอคืนเงินทอน)
        </button>
        <button type="button" disabled={submitting || !ready} style={secondaryButton} onClick={() => onFullUse({ countsOut: out, reason })}>
          ใช้พอดี ไม่มีเงินทอน
        </button>
      </div>
    </div>
  );
}

/** แลกเงิน — สองฝั่งต้องรวมเท่ากันเป๊ะถึงจะบันทึกได้ (ไม่กระทบยอดรวม "ควรมี" เลย แค่สลับชนิดแบงก์) */
function ExchangeForm({ onSubmit, submitting }) {
  const [out, setOut] = useState(emptyCounts());
  const [inCounts, setInCounts] = useState(emptyCounts());
  const outTotal = sumCounts(out);
  const inTotal = sumCounts(inCounts);
  const balanced = outTotal > 0 && outTotal === inTotal;

  return (
    <div>
      <div style={movementFormCardStyle}>
        <div style={{ fontWeight: 600, marginBottom: '0.5rem', color: 'var(--text)' }}>แบงก์ที่เอาออก</div>
        <CashDenominationTapGrid counts={out} onChange={setOut} />
        <div style={{ marginTop: '0.5rem', color: 'var(--text-2)' }}>รวมออก {formatBaht(outTotal)} ฿</div>
      </div>
      <div style={movementFormCardStyle}>
        <div style={{ fontWeight: 600, marginBottom: '0.5rem', color: 'var(--text)' }}>แบงก์ที่ได้มาแทน</div>
        <CashDenominationTapGrid counts={inCounts} onChange={setInCounts} />
        <div style={{ marginTop: '0.5rem', color: balanced ? 'var(--ok)' : 'var(--danger)' }}>
          รวมเข้า {formatBaht(inTotal)} ฿{!balanced && outTotal > 0 && inTotal > 0 ? ' — ยอดไม่เท่ากัน' : ''}
        </div>
      </div>
      <button type="button" disabled={submitting || !balanced} style={primaryButton(submitting || !balanced)} onClick={() => onSubmit({ countsOut: out, countsIn: inCounts })}>
        บันทึก
      </button>
    </div>
  );
}

/** เติมเงินทอน (paid_in, เข้าอย่างเดียว ไม่บังคับเหตุผล) / นำเงินออก (cash_drop, ออกอย่างเดียว
 * บังคับเหตุผล) — ใช้ฟอร์มเดียวกัน ต่างกันแค่ทิศทางกับว่าบังคับเหตุผลไหม */
function SimpleMovementForm({ direction, requireReason, onSubmit, submitting }) {
  const [amountCounts, setAmountCounts] = useState(emptyCounts());
  const [reason, setReason] = useState('');
  const total = sumCounts(amountCounts);
  const ready = total > 0 && (!requireReason || reason.trim().length > 0);

  return (
    <div>
      <div style={movementFormCardStyle}>
        <CashDenominationTapGrid counts={amountCounts} onChange={setAmountCounts} />
      </div>
      <div style={{ fontWeight: 700, marginBottom: '0.75rem', color: 'var(--text)' }}>รวม {formatBaht(total)} ฿</div>
      <input
        className="dark-input"
        placeholder={requireReason ? 'เหตุผล — บังคับ' : 'เหตุผล (ถ้ามี)'}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        style={movementInputStyle}
      />
      <button
        type="button"
        disabled={submitting || !ready}
        style={primaryButton(submitting || !ready)}
        onClick={() => onSubmit(direction === 'in' ? { countsIn: amountCounts, reason } : { countsOut: amountCounts, reason })}
      >
        บันทึก
      </button>
    </div>
  );
}

/** คืนเงินทอน (paid_out ขั้น 2) — กรอก "ใช้จริง" แล้วแตะแบงก์ที่ได้คืนมา ถ้าใช้จริงเกินยอดเบิก
 * สลับเป็นโหมด "ปิดรายการ + เบิกคืนพนักงานส่วนเกิน" แทน (เรียก backend คนละ endpoint) */
function ReturnForm({ movement, onReturn, onCloseAndReimburse, submitting }) {
  const [actualUsedText, setActualUsedText] = useState('');
  const [inCounts, setInCounts] = useState(emptyCounts());
  const [reimburseCounts, setReimburseCounts] = useState(emptyCounts());
  const [discrepancyReason, setDiscrepancyReason] = useState('');

  const used = Number(actualUsedText) || 0;
  const hasUsedInput = actualUsedText.trim().length > 0;
  const over = hasUsedInput && used > movement.requestedAmount;
  const expectedReturn = Math.max(0, round2(movement.requestedAmount - used));
  const returnedTotal = sumCounts(inCounts);
  const diff = round2(returnedTotal - expectedReturn);
  const mismatch = !over && Math.abs(diff) > 0.001;
  const excess = over ? round2(used - movement.requestedAmount) : 0;
  const reimburseTotal = sumCounts(reimburseCounts);

  return (
    <div>
      <div style={{ marginBottom: '0.75rem', color: 'var(--text-2)' }}>
        เบิกไป {formatBaht(movement.requestedAmount)} ฿ — {movement.reason} ({movement.actor})
      </div>
      <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--muted)', marginBottom: '0.25rem' }}>ใช้จริง (บาท)</label>
      <input
        className="dark-input"
        inputMode="decimal"
        value={actualUsedText}
        onChange={(e) => setActualUsedText(e.target.value.replace(/[^\d.]/g, ''))}
        style={movementInputStyle}
      />

      {!over && hasUsedInput && (
        <>
          <div style={{ fontWeight: 600, margin: '0.5rem 0', color: 'var(--text)' }}>
            แตะแบงก์ที่ได้คืนมา (ควรได้ {formatBaht(expectedReturn)} ฿)
          </div>
          <CashDenominationTapGrid counts={inCounts} onChange={setInCounts} />
          <div style={{ margin: '0.5rem 0', color: mismatch ? 'var(--danger)' : 'var(--ok)' }}>
            คืนมา {formatBaht(returnedTotal)} ฿{mismatch ? ` — ต่างจากที่ควรคืน ${formatBaht(diff)} บาท` : ''}
          </div>
          {mismatch && (
            <input
              className="dark-input"
              placeholder="เหตุผลส่วนต่าง — บังคับ"
              value={discrepancyReason}
              onChange={(e) => setDiscrepancyReason(e.target.value)}
              style={movementInputStyle}
            />
          )}
          <button
            type="button"
            disabled={submitting || (mismatch && !discrepancyReason.trim())}
            style={primaryButton(submitting || (mismatch && !discrepancyReason.trim()))}
            onClick={() => onReturn({ actualUsed: used, countsIn: inCounts, discrepancyReason: mismatch ? discrepancyReason : undefined })}
          >
            บันทึก
          </button>
        </>
      )}

      {over && (
        <>
          <div style={{ color: 'var(--warn)', fontWeight: 600, margin: '0.5rem 0' }}>
            ใช้จริงเกินยอดเบิก {formatBaht(excess)} บาท ถ้าพนักงานออกเงินส่วนตัวไปสมทบ ให้ปิดรายการนี้ก่อน
            แล้วเบิกคืนให้พนักงาน {formatBaht(excess)} บาท
          </div>
          <div style={{ fontWeight: 600, margin: '0.5rem 0', color: 'var(--text)' }}>
            แตะแบงก์ที่หยิบจากลิ้นชักไปคืนพนักงาน ({formatBaht(excess)} ฿)
          </div>
          <CashDenominationTapGrid counts={reimburseCounts} onChange={setReimburseCounts} />
          <div style={{ margin: '0.5rem 0', color: 'var(--text-2)' }}>รวม {formatBaht(reimburseTotal)} ฿</div>
          <button
            type="button"
            disabled={submitting || Math.abs(reimburseTotal - excess) > 0.001}
            style={primaryButton(submitting || Math.abs(reimburseTotal - excess) > 0.001)}
            onClick={() => onCloseAndReimburse({ actualUsed: used, reimburseCountsOut: reimburseCounts })}
          >
            ปิดรายการนี้ + เบิกคืนพนักงาน {formatBaht(excess)} บาท
          </button>
        </>
      )}
    </div>
  );
}

/** เหตุผลตอนยกเลิกรายการ — บังคับกรอกเสมอ (ใช้กับทุก type ของ movement) */
function CancelReasonForm({ onConfirm, submitting }) {
  const [reason, setReason] = useState('');
  return (
    <div>
      <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--muted)', marginBottom: '0.25rem' }}>เหตุผลที่ยกเลิก</label>
      <input className="dark-input" autoFocus value={reason} onChange={(e) => setReason(e.target.value)} style={movementInputStyle} />
      <button type="button" disabled={submitting || !reason.trim()} style={primaryButton(submitting || !reason.trim())} onClick={() => onConfirm(reason)}>
        ยืนยันยกเลิก
      </button>
    </div>
  );
}

const MOVEMENT_TYPE_LABELS = { paid_out: 'เบิกใช้', exchange: 'แลกเงิน', paid_in: 'เติมเงินทอน', cash_drop: 'นำเงินออก' };
const MOVEMENT_STATUS_LABELS = { pending: 'เบิกค้าง', closed: 'ปิดแล้ว', cancelled: 'ยกเลิก' };

export default function CashDrawer({ apiUrl }) {
  const { isAllBranches, effectiveBranchId, isOwner } = useBranch();
  const confirm = useConfirm();
  const blocked = isAllBranches || !effectiveBranchId;

  const [current, setCurrent] = useState(null); // { session, cashSalesSoFar, expectedNow, suggestedOpening, movementsSummary }
  const [history, setHistory] = useState([]);
  const [movements, setMovements] = useState([]); // รายการเบิกใช้/แลกเงิน/เติม/นำออกทั้งหมดของกะที่เปิดอยู่
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // เบิกใช้/แลกเงิน/เติมเงินทอน/นำเงินออก — ไม่ใช่ยอดขาย แยกออกจาก state ฟอร์มปิดร้านโดยสิ้นเชิง
  const [activeMovementForm, setActiveMovementForm] = useState(null); // 'paid_out' | 'exchange' | 'paid_in' | 'cash_drop' | null
  const [returnTarget, setReturnTarget] = useState(null); // movement ที่กำลังคืนเงินทอน (เบิกค้าง) หรือ null
  const [cancelTarget, setCancelTarget] = useState(null); // movement ที่กำลังจะยกเลิก หรือ null
  const [movementSubmitting, setMovementSubmitting] = useState(false);
  const [forceCloseReason, setForceCloseReason] = useState('');

  const [counts, setCounts] = useState(emptyCounts());
  const [touched, setTouched] = useState(false);
  const [counterName, setCounterName] = useState(readSavedName());
  const [closing, setClosing] = useState(false);
  const [withdrawals, setWithdrawals] = useState('');
  const [note, setNote] = useState('');
  const [unsyncedCount, setUnsyncedCount] = useState(0);
  const [lastClosed, setLastClosed] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const api = useCallback(async (path, options = {}) => {
    const response = await fetch(`${apiUrl}/api/cash-drawer${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('adminToken')}`
      }
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(body?.error?.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง');
    return body.data;
  }, [apiUrl]);

  const loadAll = useCallback(async () => {
    try {
      const [cur, list] = await Promise.all([api('/current'), api('/?limit=30')]);
      setCurrent(cur);
      setHistory(list);
      setMovements(cur?.session ? await api(`/movements?sessionId=${cur.session.id}`) : []);
      return cur;
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
      return null;
    } finally {
      setLoading(false);
    }
  }, [api]);

  // ตอนเข้าหน้า: ยังไม่มีกะ → เติมฟอร์มเปิดร้านด้วยจำนวนที่นับตอนปิดครั้งก่อน (ลดการนับซ้ำ)
  useEffect(() => {
    if (blocked) { setLoading(false); return; }
    setLoading(true);
    setClosing(false);
    setTouched(false);
    loadAll().then(cur => {
      if (!cur) return;
      if (cur.session) {
        setCounts(emptyCounts());
      } else {
        const s = cur.suggestedOpening || {};
        setCounts(Object.fromEntries(DENOMINATIONS.map(d => [String(d.value), s[String(d.value)] ? String(s[String(d.value)]) : ''])));
      }
    });
  }, [blocked, effectiveBranchId, loadAll]);

  // ยอดเงินสดที่ขายได้อัปเดตสดเมื่อมีบิลใหม่
  useSocket(apiUrl, {
    'transaction:created': () => { if (current?.session) loadAll(); }
  }, {
    token: localStorage.getItem('adminToken'),
    branchId: blocked ? null : effectiveBranchId
  });

  const changeCounts = (next) => { setCounts(next); setTouched(true); };

  const total = sumCounts(counts);
  const session = current?.session || null;

  const otherAdjustments = current?.movementsSummary?.otherAdjustments || null;

  // "ควรมี" ต่อมูลค่า = ตอนเปิด + รับเข้า − ทอนออก (จาก cashBreakdown ของบิลที่ระบุไว้) ± รายการ
  // เบิกใช้/แลกเงิน/เติม/นำออกที่ไม่ใช่ยอดขาย — ใช้ทั้งในการ์ดสรุปและเป็น hint ในฟอร์มนับปิดร้าน
  // จุดเดียว กันคำนวณสองที่ไม่ตรงกัน
  const shouldHaveCounts = (!session || !current?.cashFlow) ? null : {
    opening: session.openingCounts,
    should: Object.fromEntries(DENOMINATIONS.map(d => [
      String(d.value),
      countOf(session.openingCounts, d.value) + countOf(current.cashFlow.received, d.value) - countOf(current.cashFlow.change, d.value)
        + signedCount(otherAdjustments, d.value)
    ]))
  };

  const startClosing = async () => {
    // บิลที่คิดตอนออฟไลน์แล้วยังไม่ขึ้น server จะยังไม่อยู่ในยอดเงินสด — เตือนก่อนปิดกะ
    try {
      const all = await getAllTransactions();
      setUnsyncedCount(all.filter(t =>
        t.branchId === effectiveBranchId && ['pending', 'syncing', 'failed', 'conflict', 'needs_review'].includes(t.syncStatus)
      ).length);
    } catch {
      setUnsyncedCount(0);
    }
    setCounts(emptyCounts());
    setTouched(false);
    setWithdrawals('');
    setNote('');
    setClosing(true);
  };

  const handleOpen = async () => {
    if (!(await confirm(`เปิดร้านด้วยเงินตั้งต้น ${formatBaht(total)} บาท ใช่ไหม`, { title: 'เปิดร้าน', confirmText: 'เปิดร้าน' }))) return;
    setSubmitting(true);
    try {
      saveName(counterName.trim());
      await api('/open', { method: 'POST', body: JSON.stringify({ counts, openedBy: counterName.trim() }) });
      setLastClosed(null);
      setMessage({ type: 'success', text: 'เปิดร้านแล้ว' });
      await loadAll();
      setCounts(emptyCounts());
      setTouched(false);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  const withdrawalsNum = Number(withdrawals) > 0 ? Number(withdrawals) : 0;
  const expectedNow = current?.expectedNow ?? null;
  const expectedAfterWithdrawals = expectedNow === null ? null : Math.round((expectedNow - withdrawalsNum) * 100) / 100;
  const liveDiff = expectedAfterWithdrawals === null ? null : Math.round((total - expectedAfterWithdrawals) * 100) / 100;

  const pendingMovements = current?.movementsSummary?.pending || [];

  const handleClose = async (force = false) => {
    const view = differenceView(liveDiff);
    const unsyncedNote = unsyncedCount > 0 ? `\n\n⚠️ ยังมีบิลรอซิงค์ ${unsyncedCount} รายการ ยอดเงินสดอาจยังไม่ครบ` : '';
    if (!force && !(await confirm(
      `นับได้ ${formatBaht(total)} บาท · ที่ควรมี ${formatBaht(expectedAfterWithdrawals)} บาท\nผล: ${view.text}${unsyncedNote}`,
      { title: 'ปิดร้านและบันทึกการนับ?', confirmText: 'ปิดร้าน', danger: liveDiff !== 0 }
    ))) return;
    setSubmitting(true);
    try {
      saveName(counterName.trim());
      const result = await api('/close', {
        method: 'POST',
        body: JSON.stringify({
          counts, withdrawals: withdrawalsNum, note: note.trim(), closedBy: counterName.trim(),
          ...(force ? { force: true, forceReason: forceCloseReason.trim() } : {})
        })
      });
      setLastClosed(result);
      setClosing(false);
      setForceCloseReason('');
      await loadAll().then(cur => {
        if (!cur) return;
        const s = cur.suggestedOpening || {};
        setCounts(Object.fromEntries(DENOMINATIONS.map(d => [String(d.value), s[String(d.value)] ? String(s[String(d.value)]) : ''])));
      });
      setTouched(false);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- เบิกใช้/แลกเงิน/เติมเงินทอน/นำเงินออก ----------
  const runMovementAction = async (action) => {
    setMovementSubmitting(true);
    try {
      await action();
      await loadAll();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setMovementSubmitting(false);
    }
  };

  const handleMovementSubmit = (type, payload) => runMovementAction(async () => {
    await api('/movements', { method: 'POST', body: JSON.stringify({ type, ...payload, actorName: counterName.trim() }) });
    setActiveMovementForm(null);
  });

  const handleFullUseShortcut = (payload) => runMovementAction(async () => {
    const created = await api('/movements', { method: 'POST', body: JSON.stringify({ type: 'paid_out', ...payload, actorName: counterName.trim() }) });
    await api(`/movements/${created.id}/full-use`, { method: 'POST', body: JSON.stringify({ actorName: counterName.trim() }) });
    setActiveMovementForm(null);
  });

  const handleReturn = (payload) => runMovementAction(async () => {
    await api(`/movements/${returnTarget.id}/return`, { method: 'POST', body: JSON.stringify({ ...payload, actorName: counterName.trim() }) });
    setReturnTarget(null);
  });

  const handleCloseAndReimburse = (payload) => runMovementAction(async () => {
    await api(`/movements/${returnTarget.id}/close-and-reimburse`, { method: 'POST', body: JSON.stringify({ ...payload, actorName: counterName.trim() }) });
    setReturnTarget(null);
  });

  const handleCancelMovement = (reason) => runMovementAction(async () => {
    await api(`/movements/${cancelTarget.id}/cancel`, { method: 'POST', body: JSON.stringify({ reason, actorName: counterName.trim() }) });
    setCancelTarget(null);
  });

  if (blocked) {
    return (
      <div style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto' }}>
        <div style={{ padding: '1.25rem', background: '#1d1a12', border: '1px solid #4a3c1c', color: 'var(--warn)', borderRadius: '10px', fontWeight: 600 }}>
          ⚠️ กรุณาเลือกสาขาที่แน่นอนจากเมนูด้านบนก่อน — เงินในลิ้นชักนับแยกตามสาขา จะเลือก "รวมทุกสาขา" ไม่ได้
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 'clamp(0.5rem, 3vw, 1.5rem)', maxWidth: '1000px', margin: '0 auto', width: '100%' }}>
      <Toast message={message} onDismiss={() => setMessage(null)} />

      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h2>เงินทอนในลิ้นชัก</h2>
          <p>นับเหรียญ/แบงก์ตอนเปิดร้านและปิดร้าน แล้วเทียบกับยอดเงินสดที่ขายได้</p>
        </div>
        <CashDrawerOpenButton apiUrl={apiUrl} />
      </div>

      {loading && <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--muted)' }}>กำลังโหลด...</div>}

      {!loading && lastClosed && (() => {
        const view = differenceView(lastClosed.difference);
        return (
          <div style={{ ...cardStyle, borderColor: view.color }}>
            <div style={{ color: 'var(--muted)', marginBottom: '0.25rem' }}>ปิดร้านเรียบร้อย</div>
            <div style={{ fontSize: '1.6rem', fontWeight: 700, color: view.color }}>{view.text}</div>
            <SummaryRow label="ที่ควรมี" value={`${formatBaht(lastClosed.expectedTotal)} ฿`} />
            <SummaryRow label="นับได้" value={`${formatBaht(lastClosed.closingTotal)} ฿`} />
            <div style={{ color: 'var(--muted)', fontSize: '0.85rem', marginTop: '0.5rem' }}>
              ตัวเลขด้านล่างถูกเติมจากการนับครั้งนี้ไว้เป็นเงินตั้งต้นของกะถัดไปแล้ว
            </div>
          </div>
        );
      })()}

      {/* ยังไม่เปิดร้าน → นับเงินตั้งต้น */}
      {!loading && !session && (
        <div style={cardStyle}>
          <h3 style={{ margin: '0 0 0.25rem' }}>เปิดร้าน: นับเงินตั้งต้น</h3>
          <p style={{ margin: '0 0 1rem', color: 'var(--muted)', fontSize: '0.9rem' }}>
            กรอกจำนวนเหรียญ/แบงก์ที่เตรียมไว้ทอนในลิ้นชัก (เติมจากที่นับตอนปิดร้านครั้งก่อนให้แล้ว ตรวจให้ตรงของจริง)
          </p>
          <CountsForm counts={counts} onChange={changeCounts} />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center', marginTop: '1rem' }}>
            <input
              className="dark-input"
              type="text"
              placeholder="ชื่อผู้นับ"
              value={counterName}
              onChange={(e) => setCounterName(e.target.value)}
              maxLength={60}
              style={{ flex: '1 1 180px', minHeight: '48px' }}
            />
            <div style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--text)' }}>รวม {formatBaht(total)} ฿</div>
            <button
              type="button"
              disabled={submitting || (total === 0 && !touched)}
              onClick={handleOpen}
              style={primaryButton(submitting || (total === 0 && !touched))}
            >
              {submitting ? 'กำลังบันทึก...' : 'เปิดร้าน'}
            </button>
          </div>
        </div>
      )}

      {/* เปิดร้านอยู่ */}
      {!loading && session && (
        <Fragment>
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <h3 style={{ margin: 0 }}>กะที่เปิดอยู่</h3>
            <span style={{ color: 'var(--muted)', fontSize: '0.9rem' }}>
              เปิดเมื่อ {fmtDateTime(session.openedAt)}{session.openedBy ? ` · ${session.openedBy}` : ''}
            </span>
          </div>
          <SummaryRow label="เงินตั้งต้น" value={`${formatBaht(session.openingTotal)} ฿`} />
          <SummaryRow label="ยอดเงินสดที่ขายได้" value={`${formatBaht(current.cashSalesSoFar)} ฿`} />
          <SummaryRow label="เงินที่ควรมีในลิ้นชักตอนนี้" value={`${formatBaht(current.expectedNow)} ฿`} strong color="var(--accent)" />

          {current.cashFlow && (current.cashFlow.receivedTotal > 0 || current.cashFlow.changeTotal > 0) && (
            <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--border)' }}>
              <div style={{ fontWeight: 700, marginBottom: '0.5rem', color: 'var(--text)' }}>
                แบงก์/เหรียญที่รับ-ทอนระหว่างกะ
                <span style={{ fontWeight: 500, color: 'var(--muted)', fontSize: '0.85rem' }}>
                  {' '}(นับจาก {current.cashFlow.billsCounted} บิลที่ระบุไว้ตอนคิดเงิน)
                </span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2rem' }}>
                <CountsBreakdown title={`รับมา (${formatBaht(current.cashFlow.receivedTotal)} ฿)`} counts={current.cashFlow.received} />
                <CountsBreakdown title={`ทอนไป (${formatBaht(current.cashFlow.changeTotal)} ฿)`} counts={current.cashFlow.change} />
              </div>
            </div>
          )}

          {current.movementsSummary && (current.movementsSummary.totalsByType.paid_out !== 0
            || current.movementsSummary.totalsByType.paid_in !== 0
            || current.movementsSummary.totalsByType.cash_drop !== 0
            || current.movementsSummary.totalsByType.exchange.count > 0) && (
            <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--border)' }}>
              {current.movementsSummary.totalsByType.paid_out !== 0 && (
                <SummaryRow label="เบิกใช้" value={`${formatBaht(current.movementsSummary.totalsByType.paid_out)} ฿`} color="var(--danger)" />
              )}
              {current.movementsSummary.totalsByType.paid_in !== 0 && (
                <SummaryRow label="เติม" value={`+${formatBaht(current.movementsSummary.totalsByType.paid_in)} ฿`} color="var(--ok)" />
              )}
              {current.movementsSummary.totalsByType.cash_drop !== 0 && (
                <SummaryRow label="นำออก" value={`${formatBaht(current.movementsSummary.totalsByType.cash_drop)} ฿`} color="var(--danger)" />
              )}
              {current.movementsSummary.totalsByType.exchange.count > 0 && (
                <SummaryRow label="แลกเงิน" value={`${current.movementsSummary.totalsByType.exchange.count} ครั้ง`} />
              )}
            </div>
          )}

          {pendingMovements.length > 0 && (
            <div style={{ marginTop: '1rem', padding: '0.75rem 1rem', background: '#1d1a12', border: '1px solid #4a3c1c', borderRadius: '10px' }}>
              <div style={{ color: 'var(--warn)', fontWeight: 700, marginBottom: '0.5rem' }}>
                ⏳ เบิกค้าง {pendingMovements.length} รายการ ({formatBaht(pendingMovements.reduce((s, p) => s + p.requestedAmount, 0))} ฿)
              </div>
              {pendingMovements.map(p => (
                <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', fontSize: '0.9rem', color: 'var(--text-2)', padding: '0.25rem 0' }}>
                  <span>{formatBaht(p.requestedAmount)} ฿ — {p.reason} ({p.actor}, {fmtDateTime(p.createdAt)})</span>
                  <button type="button" onClick={() => setReturnTarget(p)} style={{ ...secondaryButton, minHeight: '36px', padding: '0 0.75rem', fontSize: '0.85rem' }}>คืนเงินทอน</button>
                </div>
              ))}
            </div>
          )}

          <div style={{ marginTop: '1rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            <button type="button" onClick={() => setActiveMovementForm('paid_out')} style={secondaryButton}>เบิกใช้</button>
            <button type="button" onClick={() => setActiveMovementForm('exchange')} style={secondaryButton}>แลกเงิน</button>
            <button type="button" onClick={() => setActiveMovementForm('paid_in')} style={secondaryButton}>เติมเงินทอน</button>
            <button type="button" onClick={() => setActiveMovementForm('cash_drop')} style={secondaryButton}>นำเงินออก</button>
          </div>

          {!closing && (
            <div style={{ marginTop: '1rem' }}>
              <button type="button" onClick={startClosing} style={primaryButton(false)}>ปิดร้าน / นับเงิน</button>
            </div>
          )}

          {closing && (
            <div style={{ marginTop: '1.25rem', paddingTop: '1.25rem', borderTop: '1px solid var(--border)' }}>
              <h3 style={{ margin: '0 0 0.75rem' }}>ปิดร้าน: นับเงินในลิ้นชัก</h3>

              {unsyncedCount > 0 && (
                <div style={{ padding: '0.75rem 1rem', marginBottom: '1rem', background: '#1d1a12', border: '1px solid #4a3c1c', color: 'var(--warn)', borderRadius: '10px', fontWeight: 600 }}>
                  ⚠️ ยังมีบิลที่คิดตอนออฟไลน์รอซิงค์ {unsyncedCount} รายการ ยอดเงินสดที่ขายได้ด้านบนอาจยังไม่ครบ
                  ควรรอให้ซิงค์เสร็จก่อนปิดร้าน
                </div>
              )}

              <CountsForm counts={counts} onChange={changeCounts} hints={shouldHaveCounts?.should} />

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.75rem', marginTop: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--muted)', marginBottom: '0.25rem' }}>เงินนำออก/ฝากออก (ถ้ามี)</label>
                  <input
                    className="dark-input"
                    type="text"
                    inputMode="decimal"
                    placeholder="0"
                    value={withdrawals}
                    onChange={(e) => setWithdrawals(e.target.value.replace(/[^\d.]/g, ''))}
                    style={{ width: '100%', minHeight: '48px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--muted)', marginBottom: '0.25rem' }}>ชื่อผู้นับ</label>
                  <input
                    className="dark-input"
                    type="text"
                    value={counterName}
                    onChange={(e) => setCounterName(e.target.value)}
                    maxLength={60}
                    style={{ width: '100%', minHeight: '48px' }}
                  />
                </div>
              </div>
              <div style={{ marginTop: '0.75rem' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--muted)', marginBottom: '0.25rem' }}>หมายเหตุ</label>
                <input
                  className="dark-input"
                  type="text"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  maxLength={500}
                  placeholder="เช่น ทอนเกินโต๊ะ 12"
                  style={{ width: '100%', minHeight: '48px' }}
                />
              </div>

              {(() => {
                const view = differenceView(liveDiff);
                return (
                  <div style={{ marginTop: '1rem', padding: '1rem', background: 'var(--surface-2)', border: `1px solid ${touched ? view.color : 'var(--border)'}`, borderRadius: '12px' }}>
                    <SummaryRow label="ที่ควรมี" value={`${formatBaht(expectedAfterWithdrawals)} ฿`} />
                    <SummaryRow label="นับได้" value={`${formatBaht(total)} ฿`} />
                    <SummaryRow label="ผล" value={touched ? view.text : 'ยังไม่ได้นับ'} strong color={touched ? view.color : 'var(--muted)'} />
                  </div>
                );
              })()}

              {touched && shouldHaveCounts && (() => {
                const diffRows = DENOMINATIONS
                  .map(d => ({ d, typed: countOf(counts, d.value), should: signedCount(shouldHaveCounts.should, d.value) }))
                  .filter(r => r.typed !== r.should);
                if (diffRows.length === 0) return null;
                return (
                  <div style={{ marginTop: '0.75rem', padding: '0.75rem 1rem', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: '10px' }}>
                    <div style={{ fontWeight: 700, marginBottom: '0.35rem', color: 'var(--text)' }}>ต่างจากที่ควรมี:</div>
                    {diffRows.map(r => (
                      <div key={r.d.value} style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', fontSize: '0.9rem', color: 'var(--text-2)' }}>
                        <span>{denominationLabel(r.d)}</span>
                        <span style={{ color: r.typed > r.should ? 'var(--warn)' : 'var(--danger)', fontWeight: 600 }}>
                          นับได้ {r.typed} / ควรมี {r.should} ({r.typed > r.should ? '+' : ''}{r.typed - r.should})
                        </span>
                      </div>
                    ))}
                  </div>
                );
              })()}

              {pendingMovements.length > 0 && (
                <div style={{ marginTop: '1rem', padding: '0.75rem 1rem', background: '#1d1a12', border: '1px solid #4a3c1c', borderRadius: '10px' }}>
                  <div style={{ color: 'var(--warn)', fontWeight: 700, marginBottom: '0.5rem' }}>
                    ⚠ มีเบิกใช้ {pendingMovements.length} รายการยังไม่คืนเงินทอน ปิดร้านตามปกติไม่ได้
                  </div>
                  {pendingMovements.map(p => (
                    <div key={p.id} style={{ fontSize: '0.9rem', color: 'var(--text-2)', padding: '0.15rem 0' }}>
                      {formatBaht(p.requestedAmount)} ฿ — {p.reason} ({p.actor})
                    </div>
                  ))}
                  {isOwner && (
                    <div style={{ marginTop: '0.75rem' }}>
                      <input
                        className="dark-input"
                        placeholder="เหตุผลที่ต้องปิดร้านทั้งที่มีเบิกค้าง — บังคับ"
                        value={forceCloseReason}
                        onChange={(e) => setForceCloseReason(e.target.value)}
                        style={{ width: '100%', minHeight: '44px' }}
                      />
                    </div>
                  )}
                </div>
              )}

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginTop: '1rem' }}>
                {pendingMovements.length === 0 ? (
                  <button type="button" disabled={submitting || !touched} onClick={() => handleClose(false)} style={primaryButton(submitting || !touched)}>
                    {submitting ? 'กำลังบันทึก...' : 'ยืนยันปิดร้าน'}
                  </button>
                ) : isOwner ? (
                  <button
                    type="button"
                    disabled={submitting || !touched || !forceCloseReason.trim()}
                    onClick={() => handleClose(true)}
                    style={primaryButton(submitting || !touched || !forceCloseReason.trim())}
                  >
                    {submitting ? 'กำลังบันทึก...' : 'ปิดกะแม้มีเบิกค้าง'}
                  </button>
                ) : null}
                <button type="button" disabled={submitting} onClick={() => setClosing(false)} style={secondaryButton}>ยกเลิก</button>
              </div>
            </div>
          )}
        </div>

        <ExpectedBreakdownCard shouldHaveCounts={shouldHaveCounts} cashFlow={current.cashFlow} expectedNow={current.expectedNow} otherAdjustments={otherAdjustments} />

        {movements.length > 0 && (
          <div style={cardStyle}>
            <h3 style={{ margin: '0 0 0.75rem' }}>รายการเบิกใช้/แลกเงิน/เติม/นำออกของกะนี้</h3>
            <div style={{ overflowX: 'auto' }}>
              <table className="table-dark" style={{ width: '100%', minWidth: '640px', borderCollapse: 'collapse', background: 'transparent' }}>
                <thead>
                  <tr>
                    {['เวลา', 'ประเภท', 'ยอด', 'เหตุผล', 'ผู้ทำ', 'สถานะ', ''].map(h => (
                      <th key={h} style={{ padding: '0.5rem', textAlign: 'left', background: 'var(--surface-2)', color: 'var(--text-2)', fontWeight: 700, fontSize: '0.85rem' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {movements.map(m => {
                    const cancelled = m.status === 'cancelled';
                    const rowStyle = { textDecoration: cancelled ? 'line-through' : 'none', color: cancelled ? 'var(--muted)' : 'var(--text)' };
                    return (
                      <tr key={m.id}>
                        <td style={{ padding: '0.5rem', borderBottom: '1px solid var(--border)', ...rowStyle }}>{fmtDateTime(m.createdAt)}</td>
                        <td style={{ padding: '0.5rem', borderBottom: '1px solid var(--border)', ...rowStyle }}>{MOVEMENT_TYPE_LABELS[m.type] || m.type}</td>
                        <td style={{ padding: '0.5rem', borderBottom: '1px solid var(--border)', ...rowStyle }}>
                          {m.type === 'exchange' ? `${formatBaht(m.amountOut)} ฿` : `${formatBaht(Math.abs(m.netAmount))} ฿`}
                          {m.discrepancy !== 0 && <span style={{ color: 'var(--warn)' }}> (ต่าง {formatBaht(m.discrepancy)})</span>}
                        </td>
                        <td style={{ padding: '0.5rem', borderBottom: '1px solid var(--border)', ...rowStyle }}>
                          {m.reason}
                          {cancelled && <div style={{ color: 'var(--danger)', textDecoration: 'none', fontSize: '0.85rem' }}>ยกเลิก: {m.cancelReason}</div>}
                        </td>
                        <td style={{ padding: '0.5rem', borderBottom: '1px solid var(--border)', ...rowStyle }}>{m.actor}</td>
                        <td style={{ padding: '0.5rem', borderBottom: '1px solid var(--border)', ...rowStyle }}>{MOVEMENT_STATUS_LABELS[m.status] || m.status}</td>
                        <td style={{ padding: '0.5rem', borderBottom: '1px solid var(--border)' }}>
                          {!cancelled && (
                            <button type="button" onClick={() => setCancelTarget(m)} style={{ ...secondaryButton, minHeight: '32px', padding: '0 0.6rem', fontSize: '0.8rem' }}>ยกเลิก</button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
        </Fragment>
      )}

      <Modal open={!!activeMovementForm} onClose={() => setActiveMovementForm(null)} title={activeMovementForm ? MOVEMENT_TYPE_LABELS[activeMovementForm] : ''}>
        {activeMovementForm === 'paid_out' && (
          <PaidOutForm
            submitting={movementSubmitting}
            onSubmit={(payload) => handleMovementSubmit('paid_out', payload)}
            onFullUse={handleFullUseShortcut}
          />
        )}
        {activeMovementForm === 'exchange' && (
          <ExchangeForm submitting={movementSubmitting} onSubmit={(payload) => handleMovementSubmit('exchange', payload)} />
        )}
        {activeMovementForm === 'paid_in' && (
          <SimpleMovementForm direction="in" requireReason={false} submitting={movementSubmitting} onSubmit={(payload) => handleMovementSubmit('paid_in', payload)} />
        )}
        {activeMovementForm === 'cash_drop' && (
          <SimpleMovementForm direction="out" requireReason submitting={movementSubmitting} onSubmit={(payload) => handleMovementSubmit('cash_drop', payload)} />
        )}
      </Modal>

      <Modal open={!!returnTarget} onClose={() => setReturnTarget(null)} title="คืนเงินทอน (เบิกใช้)">
        {returnTarget && (
          <ReturnForm
            movement={returnTarget}
            submitting={movementSubmitting}
            onReturn={handleReturn}
            onCloseAndReimburse={handleCloseAndReimburse}
          />
        )}
      </Modal>

      <Modal open={!!cancelTarget} onClose={() => setCancelTarget(null)} title="ยกเลิกรายการ">
        {cancelTarget && <CancelReasonForm submitting={movementSubmitting} onConfirm={handleCancelMovement} />}
      </Modal>

      {/* ประวัติกะ */}
      {!loading && (
        <div style={cardStyle}>
          <h3 style={{ margin: '0 0 0.75rem' }}>ประวัติการนับ</h3>
          {history.length === 0 ? (
            <div style={{ color: 'var(--muted)', padding: '1rem 0' }}>ยังไม่มีประวัติ</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="table-dark" style={{ width: '100%', minWidth: '640px', borderCollapse: 'collapse', background: 'transparent' }}>
                <thead>
                  <tr>
                    {['เปิด', 'ปิด', 'ผู้นับ', 'ตั้งต้น', 'ขายเงินสด', 'นับได้', 'ผล'].map((h, i) => (
                      <th key={h} style={{ padding: '0.6rem', textAlign: i >= 3 && i <= 5 ? 'right' : 'left', background: 'var(--surface-2)', color: 'var(--text-2)', fontWeight: 700, fontSize: '0.85rem' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {history.map(s => {
                    const view = differenceView(s.status === 'closed' ? s.difference : null);
                    const open = expandedId === s.id;
                    const td = { padding: '0.6rem', color: 'var(--text)', background: open ? 'var(--surface-2)' : 'transparent', borderBottom: '1px solid var(--border)' };
                    return (
                      <Fragment key={s.id}>
                        <tr onClick={() => setExpandedId(open ? null : s.id)} style={{ cursor: 'pointer' }}>
                          <td style={td}>{fmtDateTime(s.openedAt)}</td>
                          <td style={td}>{s.status === 'closed' ? fmtDateTime(s.closedAt) : <span style={{ color: 'var(--accent)', fontWeight: 600 }}>เปิดอยู่</span>}</td>
                          <td style={td}>{s.closedBy || s.openedBy || '—'}</td>
                          <td style={{ ...td, textAlign: 'right' }}>{formatBaht(s.openingTotal)}</td>
                          <td style={{ ...td, textAlign: 'right' }}>{s.cashSales === null ? '—' : formatBaht(s.cashSales)}</td>
                          <td style={{ ...td, textAlign: 'right' }}>{s.closingTotal === null ? '—' : formatBaht(s.closingTotal)}</td>
                          <td style={{ ...td, color: view.color, fontWeight: 700 }}>{view.text}</td>
                        </tr>
                        {open && (
                          <tr>
                            <td colSpan={7} style={{ padding: '0.75rem 1rem', background: 'var(--surface-2)', borderBottom: '1px solid var(--border)' }}>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2rem' }}>
                                <CountsBreakdown title={`ตอนเปิด (${formatBaht(s.openingTotal)} ฿)`} counts={s.openingCounts} />
                                {s.status === 'closed' && (
                                  <CountsBreakdown title={`ตอนปิด (${formatBaht(s.closingTotal)} ฿)`} counts={s.closingCounts} />
                                )}
                                {s.status === 'closed' && s.cashFlow && (s.cashFlow.receivedTotal > 0 || s.cashFlow.changeTotal > 0) && (
                                  <>
                                    <CountsBreakdown title={`รับมาระหว่างกะ (${formatBaht(s.cashFlow.receivedTotal)} ฿)`} counts={s.cashFlow.received} />
                                    <CountsBreakdown title={`ทอนไประหว่างกะ (${formatBaht(s.cashFlow.changeTotal)} ฿)`} counts={s.cashFlow.change} />
                                  </>
                                )}
                                {s.status === 'closed' && (
                                  <div style={{ fontSize: '0.9rem', color: 'var(--text-2)' }}>
                                    <div style={{ fontWeight: 700, color: 'var(--text)', marginBottom: '0.35rem' }}>สรุป</div>
                                    <div>ที่ควรมี {formatBaht(s.expectedTotal)} ฿</div>
                                    {s.withdrawals > 0 && <div>เงินนำออก {formatBaht(s.withdrawals)} ฿</div>}
                                    {s.note && <div style={{ marginTop: '0.35rem' }}>หมายเหตุ: {s.note}</div>}
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
